import { Button } from '@shared/components/shadcn';
import { twMerge } from '@shared/themes';
import Image from 'next/image';
import Link from 'next/link';
import type { ComponentProps, ReactNode, RefObject } from 'react';
import { Callout } from '../../../Callout/Callout';
import { LargeLogo } from '../../../Logos/Logos';

type ImageProps = {
  src: string;
  alt: string;
};

type CtaProps = {
  href: string;
  text: string;
};

type LoginLinkProps = {
  prefixText: string;
  linkText: string;
  href: string;
};

type LandingHeroProps = ComponentProps<'div'> & {
  headline: string;
  cta: CtaProps;
  image?: ImageProps;
  callout?: string;
  description?: string;
  legalText?: string;
  loginLink?: LoginLinkProps;
  loginLinkChildren?: ReactNode;
  ctaButtonRef?: RefObject<HTMLAnchorElement | null>;
  heroRef?: RefObject<HTMLDivElement | null>;
  'data-track-container'?: string;
  'data-track-stage-headline'?: string;
};

/**
 * LandingHero component - A hero section for landing pages
 *
 * Displays a responsive hero section with headline, description, CTA button, image,
 * and optional elements like callout badge, legal text, and login link.
 * The layout adapts to mobile (single column) and desktop (two columns) views.
 *
 * @param headline - Main headline text (required)
 * @param cta - CTA button configuration with href and text (required)
 * @param image - Image configuration with src and alt (required)
 * @param callout - Optional callout badge text
 * @param description - Optional description text below headline
 * @param legalText - Optional legal disclaimer text
 * @param loginLink - Optional login link configuration
 * @param loginLinkChildren - Optional custom React node for login link area
 */
function LandingHero({
  className,
  headline,
  cta,
  image,
  callout,
  description,
  legalText,
  loginLink,
  loginLinkChildren,
  ctaButtonRef,
  heroRef,
  style,
  'data-track-container': dataTrackContainer = 'Stage',
  'data-track-stage-headline': dataTrackStageHeadline,
  ...props
}: LandingHeroProps) {
  const containerClassName = twMerge('overflow-hidden grid grid-cols-1 items-center pb-4 md:pb-20', image ? 'md:grid-cols-2 md:items-start' : '', className);

  const displayLoginLink =
    loginLinkChildren ||
    (loginLink && (
      <p className="paragraph-s text-center md:text-left">
        {loginLink.prefixText}{' '}
        <Link href={loginLink.href} className="link-label-s hover:text-foreground-hover transition-colors" data-track-name="Log In" data-testid="landing-hero-login-link">
          {loginLink.linkText}
        </Link>
      </p>
    ));

  return (
    <div
      ref={heroRef}
      className={containerClassName}
      style={style}
      data-testid="landing-hero"
      data-track-container={dataTrackContainer}
      data-track-stage-headline={dataTrackStageHeadline || headline}
      {...props}
    >
      {image && (
        <div className="relative md:col-start-2 md:col-span-1 w-full md:w-auto flex-shrink-0 order-1 md:order-none flex flex-col md:h-full">
          <div className="px-4 pt-7 flex justify-start md:hidden">
            <LargeLogo className="text-accent-green" />
          </div>
          <div className="relative w-full h-72 md:h-full md:min-h-96 md:max-h-[675px]">
            <Image src={image.src} priority alt={image.alt} fill className="object-contain" sizes="(min-width: 768px) 50vw, 100vw" />
          </div>
        </div>
      )}

      <div className="px-4 pt-0 pb-5 md:pl-20 md:pt-6 md:row-start-1 flex flex-col items-center md:items-start order-2">
        <div className="hidden md:block pt-6 md:pt-7">
          <LargeLogo className="text-accent-green" />
        </div>

        <div className="flex flex-col gap-0 md:gap-2 pt-0 pb-4 md:py-8 items-center md:items-start w-full">
          {callout && (
            <div className="flex justify-center md:justify-start py-4 md:py-0 mt-0 md:mt-8">
              <Callout variant="accent">{callout}</Callout>
            </div>
          )}
          <div className="flex flex-col gap-4 md:gap-6 items-center md:items-start text-center md:text-left">
            <h1 className="heading-3 lg:heading-1 md:heading-2">{headline}</h1>
            {description && <p className="heading-5">{description}</p>}
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-center md:justify-start md:items-center gap-4 mb-0 md:mb-4 w-full">
          <div className="self-center md:self-start">
            <Button variant="accent" asChild data-track-name="Register" data-track-stage-cta-text={cta.text} data-testid="landing-hero-cta-button">
              <Link href={cta.href} ref={ctaButtonRef} id="landing-hero-cta-link" data-testid="landing-hero-cta-link">
                {cta.text}
              </Link>
            </Button>
          </div>
          {displayLoginLink && <div className="flex justify-center md:justify-start">{displayLoginLink}</div>}
        </div>

        {legalText && <p className="pt-4 md:pt-0 paragraph-s text-center md:text-left w-85 max-w-full">{legalText}</p>}
      </div>
    </div>
  );
}

export { LandingHero, type LandingHeroProps };
